import React, { useState } from 'react';
import { CheckCircle2Icon, Loader2Icon, PhoneIcon } from 'lucide-react';
import { business } from '../data/business';
import { copy } from '../data/copy';
import { services } from '../data/services';
import { useLanguage } from '../contexts/LanguageContext';
type Status = 'idle' | 'submitting' | 'success' | 'error';
interface FormState {
  name: string;
  phone: string;
  email: string;
  vehicle: string;
  service: string;
  otherService: string;
  date: string;
  time: string;
  contactMethod: string;
  language: string;
  notes: string;
}
const fieldClass = 'w-full min-h-[48px] rounded-md border border-navy-200 bg-white px-3.5 py-2.5 text-navy-900 placeholder:text-navy-400 transition-colors duration-150 ease-smooth focus:border-navy-500';
const labelClass = 'block text-sm font-semibold text-navy-800';
export function BookingForm() {
  const {
    t,
    lang
  } = useLanguage();
  const initialState: FormState = {
    name: '',
    phone: '',
    email: '',
    vehicle: '',
    service: '',
    otherService: '',
    date: '',
    time: '',
    contactMethod: 'call',
    language: lang === 'es' ? 'es' : 'en',
    notes: ''
  };
  const [form, setForm] = useState<FormState>(initialState);
  const [status, setStatus] = useState<Status>('idle');
  const update = (key: keyof FormState, value: string) => {
    setForm(current => ({
      ...current,
      [key]: value
    }));
  };
  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    try {
      await new Promise(resolve => setTimeout(resolve, 900));
      setStatus('success');
      setForm(initialState);
    } catch {
      setStatus('error');
    }
  };
  if (status === 'success') {
    return <div className="rounded-xl bg-white p-8 text-center shadow-sm ring-1 ring-navy-100" data-id="element-29" data-mp="13aywtpheru93">
        <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-green-50 text-green-700" data-id="element-30" data-mp="20s5wgk8o5isi">
          <CheckCircle2Icon width={30} height={30} aria-hidden="true" data-id="element-31" data-mp="1vyumwbch2nww" />
        </span>
        <h3 className="mt-5 font-display text-2xl font-bold text-navy-900" data-id="element-32" data-mp="3q9kof2d6f4k0">
          {t(copy.form.successTitle)}
        </h3>
        <p className="mx-auto mt-3 max-w-md text-navy-600" data-id="element-33" data-mp="3v9fg384rzy64">
          {t(copy.form.successBody)}
        </p>
        <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row" data-id="element-34" data-mp="3ggm0lmpi7i1g">
          <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-md bg-navy-900 px-6 font-bold text-white transition-colors duration-150 ease-smooth hover:bg-navy-800" data-id="element-35" data-mp="3lfox2irf4fxt">
            <PhoneIcon width={18} height={18} aria-hidden="true" data-id="element-36" data-mp="2xkdsmb53wt2g" />
            {t(copy.cta.call)} {business.phoneDisplay}
          </a>
          <button type="button" onClick={() => setStatus('idle')} className="inline-flex min-h-[48px] items-center justify-center rounded-md border border-navy-200 px-6 font-bold text-navy-900 transition-colors duration-150 ease-smooth hover:bg-navy-50" data-id="element-37" data-mp="32j75c1ckkg1k">
            {t(copy.form.bookAnother)}
          </button>
        </div>
      </div>;
  }
  return <form onSubmit={onSubmit} className="rounded-xl bg-white p-6 shadow-sm ring-1 ring-navy-100 sm:p-8" data-id="element-38" data-mp="2gc35kydj2p2l">
      <h3 className="font-display text-2xl font-bold text-navy-900" data-id="element-39" data-mp="2v0zcgs3ulw1m">
        {t(copy.form.title)}
      </h3>
      <p className="mt-2 text-navy-600" data-id="element-40" data-mp="33zufjvbliq0p">{t(copy.form.intro)}</p>

      <div className="mt-7 grid gap-5 sm:grid-cols-2" data-id="element-41" data-mp="33xzumh0bqxwm">
        <div data-id="element-42" data-mp="2z19ig4rn4mgh">
          <label className={labelClass} htmlFor="name" data-id="element-43" data-mp="2yzpzu7iihzbj">
            {t(copy.form.name)} <span className="text-gold-600" data-id="element-44" data-mp="3dyk0jwysgql6">*</span>
          </label>
          <input id="name" name="name" required autoComplete="name" value={form.name} onChange={event => update('name', event.target.value)} className={`mt-1.5 ${fieldClass}`} placeholder="Jane Doe" data-id="element-45" data-mp="3dvo309qus3x9" />
        </div>
        <div data-id="element-46" data-mp="5o7wp8fxxssb">
          <label className={labelClass} htmlFor="phone" data-id="element-47" data-mp="5or6g1vftav9">
            {t(copy.form.phone)} <span className="text-gold-600" data-id="element-48" data-mp="amsn6g5e4y3k">*</span>
          </label>
          <input id="phone" name="phone" type="tel" required autoComplete="tel" value={form.phone} onChange={event => update('phone', event.target.value)} className={`mt-1.5 ${fieldClass}`} placeholder="(410) 555-0123" data-id="element-49" data-mp="ap5gfpglmuvx" />
        </div>
        <div data-id="element-50" data-mp="3o15v3ax34dkd">
          <label className={labelClass} htmlFor="email" data-id="element-51" data-mp="3o2qmwss0liej">
            {t(copy.form.email)}{' '}
            <span className="font-normal text-navy-500" data-id="element-52" data-mp="3s2r0kb7ahpkj">
              {t(copy.form.optional)}
            </span>
          </label>
          <input id="email" name="email" type="email" autoComplete="email" value={form.email} onChange={event => update('email', event.target.value)} className={`mt-1.5 ${fieldClass}`} placeholder="you@email.com" data-id="element-53" data-mp="qze56qvrmqj" />
        </div>
        <div data-id="element-54" data-mp="2rvlzjro2mp5k">
          <label className={labelClass} htmlFor="vehicle" data-id="element-55" data-mp="2rv4590kyyl0a">
            {t(copy.form.vehicle)} <span className="text-gold-600" data-id="element-56" data-mp="2ntimwzpo1eip">*</span>
          </label>
          <input id="vehicle" name="vehicle" required value={form.vehicle} onChange={event => update('vehicle', event.target.value)} className={`mt-1.5 ${fieldClass}`} placeholder="2016 Honda Accord" data-id="element-57" data-mp="2nvv7lkmt62zg" />
        </div>
        <div className={form.service === 'other' ? '' : 'sm:col-span-2'} data-id="element-58" data-mp="245rkr4k6yqio">
          <label className={labelClass} htmlFor="service" data-id="element-59" data-mp="2461cawkvx5qh">
            {t(copy.form.serviceType)} <span className="text-gold-600" data-id="element-60" data-mp="205r9ttwawxgd">*</span>
          </label>
          <select id="service" name="service" required value={form.service} onChange={event => update('service', event.target.value)} className={`mt-1.5 ${fieldClass}`} data-id="element-61" data-mp="1v5czu6b53dfp">
            <option value="" data-id="element-62" data-mp="1gkr19jjkwdst">{t(copy.form.selectService)}</option>
            {services.map(service => <option key={service.slug} value={service.slug} data-id="element-63" data-mp="16k88u0dy7oza">
                {t(service.name)}
              </option>)}
            <option value="other" data-id="element-64" data-mp="1bii6qir253ol">{t(copy.form.other)}</option>
          </select>
        </div>
        {form.service === 'other' && <div data-id="element-65" data-mp="1z9nudk38p09g">
            <label className={labelClass} htmlFor="otherService" data-id="element-66" data-mp="1zb8dmdk3sws0">
              {t(copy.form.otherDetail)}
            </label>
            <input id="otherService" name="otherService" value={form.otherService} onChange={event => update('otherService', event.target.value)} className={`mt-1.5 ${fieldClass}`} data-id="element-67" data-mp="1pjchv95hpiqy" />
          </div>}
        <div data-id="element-68" data-mp="2my6rr4kr3smr">
          <label className={labelClass} htmlFor="date" data-id="element-69" data-mp="2mz03yl2hbaca">
            {t(copy.form.date)}
          </label>
          <input id="date" name="date" type="date" value={form.date} onChange={event => update('date', event.target.value)} className={`mt-1.5 ${fieldClass}`} data-id="element-70" data-mp="bipqakv645cg" />
        </div>
        <div data-id="element-71" data-mp="lb3x3s13s25h">
          <label className={labelClass} htmlFor="time" data-id="element-72" data-mp="ga7wpofymy4t">
            {t(copy.form.time)}
          </label>
          <input id="time" name="time" type="time" min="08:00" max="17:00" value={form.time} onChange={event => update('time', event.target.value)} className={`mt-1.5 ${fieldClass}`} data-id="element-73" data-mp="3oprg7vqtx26o" />
        </div>
        <div data-id="element-74" data-mp="3tokys2kz3jdp">
          <label className={labelClass} htmlFor="contactMethod" data-id="element-75" data-mp="3toulnhaq1af6">
            {t(copy.form.contactMethod)}
          </label>
          <select id="contactMethod" name="contactMethod" value={form.contactMethod} onChange={event => update('contactMethod', event.target.value)} className={`mt-1.5 ${fieldClass}`} data-id="element-76" data-mp="1cyvyrbba6asl">
            <option value="call" data-id="element-77" data-mp="37g5sis13q6jx">{t(copy.form.methodCall)}</option>
            <option value="text" data-id="element-78" data-mp="37echl0jgh064">{t(copy.form.methodText)}</option>
            <option value="email" data-id="element-79" data-mp="3ce7996b21ts8">{t(copy.form.methodEmail)}</option>
          </select>
        </div>
        <div data-id="element-80" data-mp="3h81pfzrgbqmk">
          <label className={labelClass} htmlFor="language" data-id="element-81" data-mp="3h7tdca35kpcf">
            {t(copy.form.preferredLanguage)}
          </label>
          <select id="language" name="language" value={form.language} onChange={event => update('language', event.target.value)} className={`mt-1.5 ${fieldClass}`} data-id="element-82" data-mp="3q76n73v6vfxo">
            <option value="en" data-id="element-83" data-mp="3yxb29aad8pq">{t(copy.form.english)}</option>
            <option value="es" data-id="element-84" data-mp="20s8llh9dnuv0">{t(copy.form.spanish)}</option>
          </select>
        </div>
        <div className="sm:col-span-2" data-id="element-85" data-mp="20ul8m8tzsvex">
          <label className={labelClass} htmlFor="notes" data-id="element-86" data-mp="25sd2h8e76rlr">
            {t(copy.form.message)}
          </label>
          <textarea id="notes" name="notes" rows={4} value={form.notes} onChange={event => update('notes', event.target.value)} className={`mt-1.5 ${fieldClass}`} placeholder={t(copy.form.messagePlaceholder)} data-id="element-87" data-mp="mx0j6isrlaxz" />
        </div>
      </div>

      {status === 'error' && <p role="alert" className="mt-5 rounded-md bg-red-50 px-4 py-3 text-sm font-medium text-red-800" data-id="element-88" data-mp="15ut9mlm7bseh">
          {t(copy.form.errorText)} {business.phoneDisplay}
        </p>}

      <button type="submit" disabled={status === 'submitting'} className="mt-7 inline-flex min-h-[52px] w-full items-center justify-center gap-2 rounded-md bg-gold-500 px-8 font-bold text-navy-950 transition-colors duration-150 ease-smooth hover:bg-gold-400 disabled:cursor-not-allowed disabled:opacity-70 sm:w-auto" data-id="element-89" data-mp="wtlpd7giss3e">
        {status === 'submitting' && <Loader2Icon width={18} height={18} aria-hidden="true" className="animate-spin" data-id="element-90" data-mp="29mpeah6exrew" />}
        {status === 'submitting' ? t(copy.form.submitting) : t(copy.form.submit)}
      </button>
      <p className="mt-3 text-sm text-navy-500" data-id="element-91" data-mp="2jev35i1dbspv">
        {t(copy.form.helpLine)}{' '}
        <a href={business.phoneHref} className="font-semibold text-navy-900 underline decoration-gold-400 decoration-2 underline-offset-2" data-id="element-92" data-mp="2ek8tpkeuka8f">
          {business.phoneDisplay}
        </a>
        . {t(copy.topbar.spanish)}.
      </p>
    </form>;
}